import { getMessage } from '@macksons/i18n';
export class ApiError extends Error {
    /** HTTP status; 0 when the request never got a response. */
    status;
    /** Stable problem `code` (an `ErrorCode`, or `network_error`). */
    code;
    title;
    detail;
    traceId;
    /** Per-field validation errors (`validation_error`). */
    fields;
    constructor(init) {
        super(init.detail ?? init.title ?? init.code);
        this.name = 'ApiError';
        this.status = init.status;
        this.code = init.code;
        this.title = init.title;
        this.detail = init.detail;
        this.traceId = init.traceId;
        this.fields = init.fields ?? [];
    }
    /** Network errors and 5xx are retryable; 4xx are final (design: Client offline). */
    get retryable() {
        return this.status === 0 || this.status >= 500;
    }
}
/** Fallback code for a non-problem error body, by HTTP status. */
export function codeForStatus(status) {
    if (status === 401)
        return 'invalid_token';
    if (status === 403)
        return 'forbidden';
    if (status === 404)
        return 'not_found';
    if (status === 409 || status === 400 || status === 422)
        return 'validation_error';
    if (status === 429)
        return 'retry_later';
    if (status === 502 || status === 503 || status === 504)
        return 'service_unavailable';
    return 'internal_error';
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
const str = (v) => (typeof v === 'string' && v !== '' ? v : undefined);
function parseFields(value) {
    if (!Array.isArray(value))
        return [];
    return value.flatMap((f) => isRecord(f) && typeof f['path'] === 'string' && typeof f['message'] === 'string'
        ? [{ path: f['path'], message: f['message'] }]
        : []);
}
/** Builds an {@link ApiError} from an error response's status and (parsed) body. */
export function toApiError(status, body) {
    if (isRecord(body) && typeof body['code'] === 'string') {
        return new ApiError({
            status,
            code: body['code'],
            title: str(body['title']),
            detail: str(body['detail']),
            traceId: str(body['traceId']),
            fields: parseFields(body['errors']),
        });
    }
    return new ApiError({ status, code: codeForStatus(status) });
}
/** Wraps a thrown fetch failure (offline, DNS, CORS, abort) as `network_error`. */
export function networkError(cause) {
    const err = new ApiError({ status: 0, code: 'network_error', detail: cause instanceof Error ? cause.message : undefined });
    err.cause = cause;
    return err;
}
/** Normalises anything thrown by an API call into an {@link ApiError}. */
export function asApiError(error) {
    return error instanceof ApiError ? error : networkError(error);
}
/**
 * Message key for an error code: `errors.<code>` when `packages/i18n` has it (checked in
 * the reference `en` locale), else `errors.internal_error`.
 */
export function errorMessageKey(code, locale = 'en') {
    const key = `errors.${code}`;
    return getMessage(locale, key) === undefined ? 'errors.internal_error' : key;
}
/**
 * Awaits an openapi-fetch call and returns `data`, throwing {@link ApiError} for an error
 * response or a network failure.
 */
export async function unwrap(call) {
    let result;
    try {
        result = await call;
    }
    catch (cause) {
        throw asApiError(cause);
    }
    if (!result.response.ok)
        throw toApiError(result.response.status, result.error);
    return result.data;
}
//# sourceMappingURL=problem.js.map